/**
 * Server Logger
 * Structured logger with data sanitization and size management for Datadog
 */
import { datadogLogs } from '@datadog/browser-logs';
import stringify from 'safe-stable-stringify';
import debug from 'debug';
import { ENV } from '../config/env';
import { log } from './consoleLogger';

const DEFAULT_OPTIONS = {
  maxDepth: 3,
  maxArrayLength: 10,
  maxStringLength: 1000,
  maxObjectKeys: 50,
  excludedKeys: [
    /^__react/,
    /^_/,
    /^Symbol/,
    'children',
    'ref',
    'key',
    /^__reactProps\$/,
    /^__reactEvents\$/,
    /^__reactInternalInstance/,
    /^__reactInternalMemoizedProps/,
    /^__reactInternalMemoizedState/,
    'eventListeners',
    'eventMarkers',
    '_valueTracker',
  ],
  sampleRate: 1,
  devMirrorToConsole: ENV === 'localhost',
  flattenData: true,
  staticContext: {},
};

function isPojo(v) {
  return Object.prototype.toString.call(v) === '[object Object]';
}

function getGlobalCtors() {
  const g = globalThis;
  return { URL: g?.URL, Element: g?.Element, Blob: g?.Blob, File: g?.File };
}

function sanitize(input, options, depth = 0, visited = new WeakSet()) {
  const { maxDepth, maxArrayLength, maxStringLength, maxObjectKeys, excludedKeys } = options;

  if (input === null || typeof input === 'undefined') return null;

  if (typeof input === 'string') {
    const limit = maxStringLength ?? 1000;
    return input.length > limit ? input.slice(0, limit) + '[Truncated]' : input;
  }
  if (typeof input === 'number' || typeof input === 'boolean') return input;
  if (typeof input === 'bigint') {
    const n = Number(input);
    return Number.isSafeInteger(n) ? n : String(input) + 'n';
  }
  if (typeof input === 'function' || typeof input === 'symbol') return undefined;

  if (input instanceof Date) return input.toISOString();
  if (input instanceof Error) {
    return {
      name: input.name,
      message: input.message,
      stack: input.stack,
      cause: input.cause ? sanitize(input.cause, options, depth + 1, visited) : undefined,
    };
  }

  const { URL: URLCtor, Element: ElementCtor, Blob: BlobCtor, File: FileCtor } = getGlobalCtors();

  if (URLCtor && input instanceof URLCtor) return input.toString();
  if (ElementCtor && input instanceof ElementCtor) {
    const el = input;
    return `[DOMElement <${el.tagName.toLowerCase()}> id="${el.id || 'none'}" class="${el.className || 'none'}"]`;
  }
  if (BlobCtor && input instanceof BlobCtor) {
    return `[Blob ${input.type} ${input.size}b]`;
  }
  if (FileCtor && input instanceof FileCtor) {
    return `[File ${input.name} ${input.type} ${input.size}b]`;
  }
  if (input instanceof Map) {
    return { type: 'Map', entries: Array.from(input.entries()).slice(0, maxArrayLength) };
  }
  if (input instanceof Set) {
    return { type: 'Set', values: Array.from(input.values()).slice(0, maxArrayLength) };
  }

  if (typeof input === 'object') {
    const obj = input;
    if (visited.has(obj)) return '[Circular]';
    if (depth > (maxDepth ?? 3)) return '[Truncated]';

    const objKeys = Object.keys(obj);
    const hasReactProps = objKeys.some(
      (key) =>
        key.startsWith('__react') ||
        key.startsWith('_react') ||
        key === 'eventListeners' ||
        key === 'eventMarkers' ||
        key === '_valueTracker',
    );

    if (hasReactProps) {
      if ('tagName' in obj && typeof obj.tagName === 'string') {
        const el = obj;
        return `[ReactDOMElement <${el.tagName?.toLowerCase() || 'unknown'}> id="${el.id || 'none'}" class="${el.className || 'none'}"]`;
      }
      return '[ReactElement]';
    }

    visited.add(obj);

    if (Array.isArray(obj)) {
      return obj.slice(0, maxArrayLength).map((v) => sanitize(v, options, depth + 1, visited));
    }

    const out = {};
    const keys = Object.keys(obj);
    const limited = keys.slice(0, maxObjectKeys);

    for (const key of limited) {
      if (excludedKeys?.some((r) => (typeof r === 'string' ? r === key : r.test(key)))) continue;
      try {
        const val = sanitize(obj[key], options, depth + 1, visited);
        if (val !== undefined) out[key] = val;
      } catch {
        out[key] = '[Error]';
      }
    }
    if (keys.length > (maxObjectKeys ?? 50)) {
      out['[Truncated]'] = `${keys.length - (maxObjectKeys ?? 50)} more keys`;
    }
    return out;
  }

  return undefined;
}

function shouldSample(sampleRate = 1) {
  if (sampleRate >= 1) return true;
  if (sampleRate <= 0) return false;
  return Math.random() < sampleRate;
}

const debuggers = {
  info: debug('app:dog:info'),
  warn: debug('app:dog:warn'),
  error: debug('app:dog:error'),
  debug: debug('app:dog:debug'),
};

function mirrorToConsole(level, label, attrs) {
  const fn = debuggers[level];
  fn(`[${level.toUpperCase()}] ${label} %O`, attrs);
}

function isDatadogInitialized() {
  try {
    return typeof datadogLogs?.logger !== 'undefined';
  } catch {
    return false;
  }
}

function trimLabel(label, max = 512) {
  return label.length > max ? label.slice(0, max) + '…' : label;
}

function baseLog(boundCtx, level, label, data, options = {}) {
  const merged = { ...DEFAULT_OPTIONS, ...options };
  if (!shouldSample(merged.sampleRate)) return;

  const sanitized = sanitize(data, merged);
  const labelSafe = trimLabel(label);

  let size = 0;
  try {
    const s = stringify(sanitized);
    size = s ? s.length : 0;
  } catch {
    // ignore preview serialization issues
  }
  if (size > 50_000) {
    log(`Large log detected: ${size} chars for "${labelSafe}"`);
  }

  const core = {
    timestamp: new Date().toISOString(),
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    logSize: size,
    ...merged.staticContext,
    ...boundCtx,
  };

  const payload = merged.flattenData && isPojo(sanitized) ? { ...core, ...sanitized } : { ...core, data: sanitized };

  // Always mirror to console in development
  if (merged.devMirrorToConsole) {
    mirrorToConsole(level, labelSafe, payload);
  }

  // Only send to Datadog if initialized
  if (isDatadogInitialized()) {
    datadogLogs.logger[level](labelSafe, payload);
  } else if (!merged.devMirrorToConsole) {
    // Fallback to console if Datadog not initialized and console mirror is off
    console[level === 'debug' ? 'log' : level](`[Datadog not initialized] ${labelSafe}`, payload);
  }
}

/**
 * Creates a logger instance with optional bound context.
 * @param {object} [boundCtx={}] - Context to bind to all log calls from this logger.
 * @returns {object} Logger instance with logging methods.
 */
export function makeLogger(boundCtx = {}) {
  return {
    /**
     * Log an informational message.
     * @param {string} label - Descriptive message for the log entry.
     * @param {object} [data] - Optional structured data to include.
     * @param {object} [options] - Optional logging options (sampling, etc.).
     */
    info: (label, data, options) => baseLog(boundCtx, 'info', label, data, options),
    /**
     * Log a warning message.
     * @param {string} label - Descriptive message for the log entry.
     * @param {object} [data] - Optional structured data to include.
     * @param {object} [options] - Optional logging options (sampling, etc.).
     */
    warn: (label, data, options) => baseLog(boundCtx, 'warn', label, data, options),
    /**
     * Log an error message.
     * @param {string} label - Descriptive message for the log entry.
     * @param {object} [data] - Optional structured data to include.
     * @param {object} [options] - Optional logging options (sampling, etc.).
     */
    error: (label, data, options) => baseLog(boundCtx, 'error', label, data, options),
    /**
     * Log a debug message.
     * @param {string} label - Descriptive message for the log entry.
     * @param {object} [data] - Optional structured data to include.
     * @param {object} [options] - Optional logging options (sampling, etc.).
     */
    debug: (label, data, options) => baseLog(boundCtx, 'debug', label, data, options),
    /**
     * Create a child logger with additional bound context.
     * @param {object} ctx - Additional context to merge with parent context.
     * @returns {object} New logger instance with merged context.
     */
    child: (ctx) => makeLogger({ ...boundCtx, ...ctx }),
  };
}

export const serverLogger = makeLogger();
